
import React from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { ChartContainer } from "@/components/ui/chart";

interface PublishingTimesProps {
  timeframe: "7d" | "30d" | "90d" | "1y";
}

const PublishingTimes: React.FC<PublishingTimesProps> = ({ timeframe }) => {
  // Generate days of the week
  const daysOfWeek = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
  
  // Generate hours of the day
  const hoursOfDay = Array.from({ length: 24 }, (_, i) => 
    i === 0 ? '12am' : i < 12 ? `${i}am` : i === 12 ? '12pm' : `${i-12}pm`
  );

  // Generate sample heatmap data (in a real app, this would come from an API)
  const generateHeatmapData = () => {
    const data = [];
    for (let day = 0; day < 7; day++) {
      for (let hour = 0; hour < 24; hour++) {
        // Generate realistic pattern where weekdays during work hours have higher values
        let value = Math.random() * 40;
        
        // Weekdays (Mon-Fri) during work hours (9am-5pm) have higher values
        if (day < 5 && hour >= 9 && hour <= 17) {
          value += Math.random() * 60;
        }
        
        // Peak times around morning (8-9am) and evening (7-8pm)
        if ((hour === 8 || hour === 19) && day < 5) {
          value += Math.random() * 50;
        }
        
        // Weekend evenings
        if ((day === 5 || day === 6) && hour >= 18 && hour <= 22) {
          value += Math.random() * 50;
        }
        
        data.push({
          day: daysOfWeek[day],
          hour: hoursOfDay[hour],
          value: Math.round(value)
        });
      }
    }
    return data;
  };

  const heatmapData = generateHeatmapData();

  // Generate publishing recommendations
  const generateRecommendations = () => {
    return [
      "Publish important articles on weekdays between 9am-10am for maximum immediate engagement",
      "Evening content (7pm-9pm) receives higher engagement and sharing rates",
      "Weekend morning posts (9am-11am) perform better than afternoon content",
      "Breaking news articles perform best when published immediately, regardless of time",
      "Long-form content receives more reads when published in the evening or weekends"
    ];
  };

  const recommendations = generateRecommendations();

  return (
    <div className="space-y-6">
      <Card className="dark:bg-gray-800 dark:text-gray-100">
        <CardHeader>
          <CardTitle>Peak Reading Times</CardTitle>
          <CardDescription>
            Heatmap showing when your readers are most active
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="w-full overflow-x-auto">
            <div className="min-w-[900px] h-[400px]">
              <div className="flex">
                <div className="w-10"></div>
                <div className="flex-1 flex">
                  {hoursOfDay.map((hour, i) => (
                    <div key={i} className="flex-1 text-center text-xs text-muted-foreground">
                      {i % 3 === 0 ? hour : ''}
                    </div>
                  ))}
                </div>
              </div>
              {daysOfWeek.map((day, dayIndex) => (
                <div key={day} className="flex h-[50px]">
                  <div className="w-10 flex items-center justify-end pr-2 text-xs text-muted-foreground">
                    {day}
                  </div>
                  <div className="flex-1 flex">
                    {hoursOfDay.map((hour, hourIndex) => {
                      const dataPoint = heatmapData.find(
                        d => d.day === day && d.hour === hour
                      );
                      const value = dataPoint ? dataPoint.value : 0;
                      const intensity = Math.min(Math.max(value / 100, 0), 1);
                      
                      // Calculate RGB color for heatmap (orange gradient)
                      const r = Math.floor(249 * intensity) + (255 - Math.floor(249 * intensity));
                      const g = Math.floor(115 * intensity);
                      const b = Math.floor(22 * intensity);
                      
                      return (
                        <div 
                          key={`${day}-${hour}`} 
                          className="flex-1 border border-border group relative"
                          style={{ 
                            backgroundColor: `rgba(${r}, ${g}, ${b}, ${intensity * 0.7})`
                          }}
                        >
                          <div className="absolute inset-0 opacity-0 group-hover:opacity-100 bg-black bg-opacity-60 flex items-center justify-center transition-opacity">
                            <span className="text-white text-xs font-medium px-1">
                              {value} readers
                            </span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div className="mt-4 flex justify-between">
            <div className="flex items-center text-xs text-muted-foreground">
              <span>Fewer readers</span>
              <div className="mx-2 flex">
                {[0.1, 0.3, 0.5, 0.7, 0.9].map((intensity, i) => {
                  const r = Math.floor(249 * intensity) + (255 - Math.floor(249 * intensity));
                  const g = Math.floor(115 * intensity);
                  const b = Math.floor(22 * intensity);
                  
                  return (
                    <div 
                      key={i}
                      className="w-5 h-3"
                      style={{ 
                        backgroundColor: `rgba(${r}, ${g}, ${b}, ${intensity * 0.7})`
                      }}
                    />
                  );
                })}
              </div>
              <span>More readers</span>
            </div>
          </div>
        </CardContent>
      </Card>
      
      <Card className="dark:bg-gray-800 dark:text-gray-100">
        <CardHeader>
          <CardTitle>Publishing Recommendations</CardTitle>
          <CardDescription>
            Optimize your publishing schedule based on reader activity
          </CardDescription>
        </CardHeader>
        <CardContent>
          <ul className="space-y-2">
            {recommendations.map((rec, index) => (
              <li key={index} className="flex items-start gap-2">
                <div className="flex-shrink-0 w-6 h-6 rounded-full bg-directpress-orange text-white flex items-center justify-center text-sm font-medium">
                  {index + 1}
                </div>
                <span>{rec}</span>
              </li>
            ))}
          </ul>
        </CardContent>
      </Card>
    </div>
  );
};

export default PublishingTimes;
